import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, ShoppingCart, Key, Mail, Settings, BarChart3,
  Search, Eye, RotateCcw, Trash2, AlertTriangle, LogOut,
  Download, Plus, RefreshCw, CheckCircle, Clock, XCircle,
  ChevronLeft, ChevronRight, ChevronUp, X, Send, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Sidebar navigation items
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Vue globale', icon: BarChart3 },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'orders', label: 'Commandes', icon: ShoppingCart },
  { id: 'tokens', label: 'Tokens', icon: Key },
  { id: 'settings', label: 'Paramètres', icon: Settings },
];

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, subvalue, color }) => (
  <div className="bg-[#1F2937] rounded-xl p-5 border border-gray-700">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {subvalue && <p className="text-gray-500 text-xs">{subvalue}</p>}
      </div>
    </div>
  </div>
);

// User Detail Modal
const TEST_LABELS = {
  disc: 'DISC',
  bigfive: 'Big Five',
  mbti: 'MBTI',
  enneagram: 'Ennéagramme',
  via: 'VIA Strengths',
  schwartz: 'Valeurs Schwartz',
};

// Extracts a short human-readable summary from a scoring dict
// (works across the 6 test formats returned by the backend)
const summariseTest = (result) => {
  if (!result || typeof result !== 'object') return null;
  const pick = (keys) => {
    for (const k of keys) {
      if (result[k] !== undefined && result[k] !== null && result[k] !== '') return result[k];
    }
    return null;
  };
  const dominant = pick(['dominant_name', 'dominant_style', 'personality_type', 'type_name', 'main_type', 'archetype']);
  const description = pick(['dominant_description', 'summary', 'type_description', 'short_description']);
  const percentages = result.percentages || result.trait_percentages || null;
  const topStrengths = result.top_strengths || result.top_5 || null;
  return { dominant, description, percentages, topStrengths };
};

const TestResultBlock = ({ testId, result }) => {
  const s = summariseTest(result);
  if (!s) return null;
  const label = TEST_LABELS[testId] || testId;
  return (
    <div className="p-4 bg-[#111827] rounded-lg" data-testid={`admin-user-result-${testId}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-orange-400 font-semibold">{label}</p>
          {s.dominant && (
            <p className="text-white text-lg font-bold">{s.dominant}</p>
          )}
        </div>
        <span className="text-green-400 text-xs">Complété</span>
      </div>
      {s.description && (
        <p className="text-gray-300 text-sm leading-relaxed">
          {String(s.description).slice(0, 260)}{String(s.description).length > 260 ? '…' : ''}
        </p>
      )}
      {s.percentages && typeof s.percentages === 'object' && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(s.percentages).slice(0, 8).map(([k, v]) => (
            <div key={k} className="text-xs">
              <span className="text-gray-400">{k}</span>
              <span className="text-white font-semibold ml-1">
                {typeof v === 'number' ? `${Math.round(v)}%` : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}
      {Array.isArray(s.topStrengths) && s.topStrengths.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {s.topStrengths.slice(0, 5).map((str, i) => (
            <span key={i} className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-300">
              {typeof str === 'string' ? str : (str?.name || str?.strength || '')}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const UserModal = ({ user, onClose }) => {
  if (!user) return null;
  const testResults = user.test_results || {};
  const testIds = Object.keys(testResults);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="admin-user-modal">
      <div className="bg-[#1F2937] rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-[#1F2937] z-10">
          <h3 className="text-xl font-bold text-white">Profil utilisateur</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg" data-testid="admin-user-modal-close">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-500 text-sm">Nom complet</p>
              <p className="text-white font-medium">{user.prenom} {user.nom}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Email</p>
              <p className="text-white font-medium">{user.email}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Situation</p>
              <p className="text-white font-medium">{user.situation || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Pack</p>
              <p className="text-white font-medium capitalize">{user.pack}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Progression</p>
              <p className="text-white font-medium">{testIds.length}/6 tests</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Inscription</p>
              <p className="text-white font-medium">
                {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
              </p>
            </div>
          </div>
          
          {/* Test Results — enriched with scores & summary */}
          {testIds.length > 0 && (
            <div>
              <h4 className="text-lg font-bold text-white mb-3">Résultats des tests</h4>
              <div className="space-y-3">
                {testIds.map((testId) => (
                  <TestResultBlock key={testId} testId={testId} result={testResults[testId]} />
                ))}
              </div>
            </div>
          )}
          {testIds.length === 0 && (
            <p className="text-gray-500 text-sm italic">Aucun test complété pour l&apos;instant.</p>
          )}
          
          {/* Admin Notes */}
          {user.notes && user.notes.length > 0 && (
            <div>
              <h4 className="text-lg font-bold text-white mb-3">Notes admin</h4>
              <div className="space-y-2">
                {user.notes.map((note, i) => (
                  <div key={i} className="p-3 bg-[#111827] rounded-lg">
                    <p className="text-gray-300">{note.note}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {note.admin_name} - {new Date(note.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Manual Access Modal
const ManualAccessModal = ({ onClose, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [prenom, setPrenom] = useState('');
  const [pack, setPack] = useState('decouverte');
  const [selectedTests, setSelectedTests] = useState(['disc', 'bigfive']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const DECOUVERTE_TESTS = [
    { id: 'disc', label: 'DISC' },
    { id: 'bigfive', label: 'Big Five' },
    { id: 'via', label: 'VIA Strengths' },
    { id: 'schwartz', label: 'Valeurs Schwartz' },
    { id: 'enneagram', label: 'Ennéagramme' },
    { id: 'mbti', label: 'MBTI' },
  ];

  const toggleTest = (id) => {
    setSelectedTests((cur) => {
      if (cur.includes(id)) return cur.filter((t) => t !== id);
      if (cur.length >= 2) return cur; // Découverte = 2 tests max
      return [...cur, id];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pack === 'decouverte' && selectedTests.length !== 2) {
      setError('Choisis exactement 2 tests pour le pack Découverte.');
      return;
    }
    setLoading(true);
    const result = await onSubmit({
      email,
      prenom: prenom.trim(),
      pack,
      selected_tests: pack === 'decouverte' ? selectedTests : null,
    });
    setLoading(false);
    if (result?.ok) {
      onClose();
    } else {
      setError(result?.error || 'Une erreur est survenue.');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1F2937] rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Créer accès manuel</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg" data-testid="manual-close-btn">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white"
              placeholder="email@exemple.com"
              data-testid="manual-email-input"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Prénom (optionnel)</label>
            <input
              type="text"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white"
              placeholder="Prénom pour l'email"
              data-testid="manual-prenom-input"
            />
          </div>
          
          <div>
            <label className="block text-gray-400 text-sm mb-2">Pack</label>
            <select
              value={pack}
              onChange={(e) => setPack(e.target.value)}
              className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white"
              data-testid="manual-pack-select"
            >
              <option value="decouverte">Découverte (2 tests)</option>
              <option value="complete">Complète (6 tests)</option>
              <option value="duo">Duo (6 tests + partenaire)</option>
            </select>
          </div>

          {pack === 'decouverte' && (
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Tests autorisés (choisis exactement 2)
              </label>
              <div className="grid grid-cols-2 gap-2">
                {DECOUVERTE_TESTS.map((tst) => {
                  const checked = selectedTests.includes(tst.id);
                  return (
                    <label
                      key={tst.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border text-sm ${
                        checked
                          ? 'bg-orange-500/20 border-orange-500 text-white'
                          : 'bg-[#111827] border-gray-700 text-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTest(tst.id)}
                        className="accent-orange-500"
                        data-testid={`manual-test-${tst.id}`}
                      />
                      {tst.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-400" data-testid="manual-error">{error}</p>
          )}
          
          <button
            type="submit"
            disabled={loading}
            data-testid="manual-submit-btn"
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            Créer l&apos;accès
          </button>
        </form>
      </div>
    </div>
  );
};

// Main Admin Dashboard

// Order type badge resolution (initial purchase vs upgrade)
const getOrderTypeBadge = (order) => {
  if (order.is_upgrade) {
    if (order.upgrade_type === 'decouverte_to_complete') {
      return { label: 'Upgrade D→C', classes: 'bg-green-500/20 text-green-300', isUpgrade: true };
    }
    if (order.upgrade_type === 'complete_to_duo') {
      return { label: 'Upgrade C→D', classes: 'bg-pink-500/20 text-pink-300', isUpgrade: true };
    }
    return { label: 'Upgrade', classes: 'bg-gray-500/20 text-gray-300', isUpgrade: true };
  }
  const map = {
    decouverte: { label: 'Découverte', classes: 'bg-blue-500/20 text-blue-300' },
    complete: { label: 'Complète', classes: 'bg-orange-500/20 text-orange-300' },
    duo: { label: 'Duo', classes: 'bg-purple-500/20 text-purple-300' },
  };
  return { ...(map[order.pack] || { label: order.pack || '-', classes: 'bg-gray-700 text-gray-300' }), isUpgrade: false };
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [kpis, setKpis] = useState(null);
  const [dailyRegistrations, setDailyRegistrations] = useState([]);
  
  // Users data
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersFilter, setUsersFilter] = useState({ situation: '', pack: '', search: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Orders data
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersRange, setOrdersRange] = useState('today');
  
  // Tokens data
  const [tokens, setTokens] = useState([]);
  const [tokensTotal, setTokensTotal] = useState(0);
  const [tokensPage, setTokensPage] = useState(1);
  
  // System health (Stripe / Brevo / DB)
  const [health, setHealth] = useState(null);
  
  // Modals
  const [showManualAccess, setShowManualAccess] = useState(false);
  
  // Auth check
  useEffect(() => {
    const token = localStorage.getItem('bpm_admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    // Verify admin token
    fetch(`${API}/api/admin/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Unauthorized');
        return res.json();
      })
      .then(data => {
        setAdmin(data.admin);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem('bpm_admin_token');
        navigate('/admin/login');
      });
  }, [navigate]);
  
  // Fetch dashboard KPIs
  useEffect(() => {
    if (!admin) return;
    
    const token = localStorage.getItem('bpm_admin_token');
    fetch(`${API}/api/admin/dashboard`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setKpis(data.kpis);
        setDailyRegistrations(data.daily_registrations || []);
      })
      .catch(console.error);
  }, [admin]);
  
  // Fetch users
  const fetchUsers = async () => {
    const token = localStorage.getItem('bpm_admin_token');
    const params = new URLSearchParams({
      page: usersPage,
      limit: 20,
      ...(usersFilter.situation && { situation: usersFilter.situation }),
      ...(usersFilter.pack && { pack: usersFilter.pack }),
      ...(usersFilter.search && { search: usersFilter.search }),
    });
    
    const response = await fetch(`${API}/api/admin/users?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setUsers(data.users || []);
    setUsersTotal(data.total || 0);
  };
  
  useEffect(() => {
    if (admin && activeTab === 'users') fetchUsers();
  }, [admin, activeTab, usersPage, usersFilter]);
  
  // Fetch orders
  const fetchOrders = async () => {
    const token = localStorage.getItem('bpm_admin_token');
    const response = await fetch(`${API}/api/admin/orders?page=${ordersPage}&range=${ordersRange}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setOrders(data.orders || []);
    setOrdersTotal(data.total || 0);
  };
  
  useEffect(() => {
    if (admin && activeTab === 'orders') fetchOrders();
  }, [admin, activeTab, ordersPage, ordersRange]);
  
  // Fetch system health (Stripe / Brevo / DB)
  useEffect(() => {
    if (!admin) return;
    fetch(`${API}/api/health`)
      .then(res => res.json())
      .then(data => setHealth(data))
      .catch(() => setHealth(null));
  }, [admin]);
  
  // Fetch tokens
  const fetchTokens = async () => {
    const token = localStorage.getItem('bpm_admin_token');
    const response = await fetch(`${API}/api/admin/tokens?page=${tokensPage}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setTokens(data.tokens || []);
    setTokensTotal(data.total || 0);
  };
  
  useEffect(() => {
    if (admin && activeTab === 'tokens') fetchTokens();
  }, [admin, activeTab, tokensPage]);
  
  // View user detail
  const handleViewUser = async (userId) => {
    const token = localStorage.getItem('bpm_admin_token');
    const response = await fetch(`${API}/api/admin/users/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setSelectedUser({ ...data.user, test_results: data.test_results, notes: data.notes });
  };
  
  // Disable user
  const handleDisableUser = async (userId) => {
    if (!window.confirm('Désactiver cet utilisateur ?')) return;
    
    const token = localStorage.getItem('bpm_admin_token');
    await fetch(`${API}/api/admin/users/${userId}/disable`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchUsers();
  };
  
  // Regenerate token
  const handleRegenerateToken = async (tokenId) => {
    const token = localStorage.getItem('bpm_admin_token');
    await fetch(`${API}/api/admin/tokens/${tokenId}/regenerate`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchTokens();
    alert('Token régénéré et email envoyé');
  };
  
  // Create manual access
  const handleCreateManualAccess = async (payload) => {
    const token = localStorage.getItem('bpm_admin_token');
    try {
      const res = await fetch(`${API}/api/admin/tokens/create-manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let detail = '';
        try {
          const data = await res.json();
          detail = data?.detail || '';
        } catch (_e) { /* ignore */ }
        return { ok: false, error: detail || `Erreur ${res.status}` };
      }
      await fetchTokens();
      alert(`Accès créé pour ${payload.email}`);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: 'Impossible de contacter le serveur.' };
    }
  };
  
  // Logout
  const handleLogout = () => {
    localStorage.removeItem('bpm_admin_token');
    localStorage.removeItem('bpm_admin_refresh');
    localStorage.removeItem('bpm_admin');
    navigate('/admin/login');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#111827] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1F2937] border-r border-gray-700 flex flex-col">
        <div className="p-6 border-b border-gray-700">
          <h1 className="text-2xl font-bold">
            <span className="text-orange-500">BPM</span>
            <span className="text-gray-400 text-sm ml-2">Admin</span>
          </h1>
        </div>
        
        <nav className="flex-1 p-4">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                activeTab === item.id
                  ? 'bg-orange-500/20 text-orange-500'
                  : 'text-gray-400 hover:bg-gray-700'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>
        
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
              <span className="text-orange-500 font-bold">
                {admin?.prenom?.charAt(0) || 'A'}
              </span>
            </div>
            <div>
              <p className="text-white text-sm font-medium">{admin?.prenom}</p>
              <p className="text-gray-500 text-xs">{admin?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        {/* Dashboard View */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-white">Vue globale</h2>
            
            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              <KPICard 
                icon={Users} 
                label="Total utilisateurs" 
                value={kpis?.total_users || 0} 
                color="bg-blue-500" 
              />
              <KPICard 
                icon={CheckCircle} 
                label="Tests complétés (6/6)" 
                value={kpis?.completed_users || 0}
                subvalue={`${kpis?.completion_rate || 0}% de complétion`}
                color="bg-green-500" 
              />
              <KPICard 
                icon={ShoppingCart} 
                label="Chiffre d'affaires" 
                value={`${((kpis?.total_revenue || 0) / 100).toFixed(0)}€`}
                color="bg-purple-500" 
              />
              <KPICard 
                icon={Key} 
                label="Tokens en attente" 
                value={kpis?.pending_tokens || 0} 
                color="bg-orange-500" 
              />
              {/* New KPI: Upgrades */}
              <div
                className="bg-gradient-to-br from-orange-500/20 to-purple-500/20 border border-orange-500/40 rounded-xl p-5"
                data-testid="kpi-upgrades-count"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-purple-500 flex items-center justify-center">
                    <ChevronUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Upgrades effectués</p>
                    <p className="text-2xl font-bold text-white">{kpis?.upgrades_total || 0}</p>
                    <p className="text-gray-400 text-xs">
                      {kpis?.upgrades_d_to_c || 0} D→C · {kpis?.upgrades_c_to_d || 0} C→D
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Revenue by pack */}
            {kpis?.revenue_by_pack && (
              <div className="bg-[#1F2937] rounded-xl p-6 border border-gray-700 mt-12">
                <h3 className="text-lg font-bold text-white mb-4">Répartition par pack</h3>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(kpis.revenue_by_pack).map(([pack, amount]) => (
                    <div key={pack} className="text-center">
                      <p className="text-gray-400 capitalize">{pack}</p>
                      <p className="text-2xl font-bold text-white">{(amount / 100).toFixed(0)}€</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Users View */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Utilisateurs</h2>
              <span className="text-gray-400">{usersTotal} total</span>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={usersFilter.search}
                  onChange={(e) => setUsersFilter(f => ({ ...f, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 bg-[#1F2937] border border-gray-700 rounded-lg text-white"
                />
              </div>
              <select
                value={usersFilter.situation}
                onChange={(e) => setUsersFilter(f => ({ ...f, situation: e.target.value }))}
                className="px-4 py-2 bg-[#1F2937] border border-gray-700 rounded-lg text-white"
              >
                <option value="">Toutes situations</option>
                <option value="etudiant">Étudiant</option>
                <option value="salarie">Salarié</option>
                <option value="entrepreneur">Entrepreneur</option>
                <option value="reconversion">Reconversion</option>
                <option value="manager">Manager</option>
              </select>
              <select
                value={usersFilter.pack}
                onChange={(e) => setUsersFilter(f => ({ ...f, pack: e.target.value }))}
                className="px-4 py-2 bg-[#1F2937] border border-gray-700 rounded-lg text-white"
              >
                <option value="">Tous packs</option>
                <option value="decouverte">Découverte</option>
                <option value="complete">Complète</option>
                <option value="duo">Duo</option>
              </select>
            </div>
            
            {/* Users Table */}
            <div className="bg-[#1F2937] rounded-xl border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#111827]">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Nom</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Email</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Situation</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Pack</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Progression</th>
                    <th className="px-4 py-3 text-right text-gray-400 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id} className="border-t border-gray-700 hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-white">{user.prenom} {user.nom}</td>
                      <td className="px-4 py-3 text-gray-400">{user.email}</td>
                      <td className="px-4 py-3 text-gray-400 capitalize">{user.situation}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          user.pack === 'groupe' ? 'bg-purple-500/20 text-purple-400' :
                          user.pack === 'trio' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-700 text-gray-300'
                        }`}>
                          {user.pack}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`${user.progress === 6 ? 'text-green-500' : 'text-orange-500'}`}>
                          {user.progress}/6
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleViewUser(user._id)}
                          data-testid={`admin-user-view-${user._id}`}
                          className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
                          title="Voir profil"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDisableUser(user._id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
                <span className="text-gray-500 text-sm">Page {usersPage}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                    disabled={usersPage === 1}
                    className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg"
                  >
                    <ChevronLeft className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => setUsersPage(p => p + 1)}
                    disabled={users.length < 20}
                    className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg"
                  >
                    <ChevronRight className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Orders View */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-2xl font-bold text-white">
                Commandes
                <span className="ml-2 text-base text-gray-400 font-normal">
                  ({ordersTotal} {
                    ordersRange === 'today' ? "aujourd'hui" :
                    ordersRange === '7d' ? 'sur 7 jours' :
                    ordersRange === '30d' ? 'sur 30 jours' : 'au total'
                  })
                </span>
              </h2>
            </div>

            {/* Date range pills */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'today', label: "Aujourd'hui" },
                { id: '7d', label: '7 derniers jours' },
                { id: '30d', label: '30 derniers jours' },
                { id: 'all', label: 'Tout' },
              ].map(opt => (
                <button
                  key={opt.id}
                  data-testid={`orders-filter-${opt.id}`}
                  onClick={() => { setOrdersPage(1); setOrdersRange(opt.id); }}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    ordersRange === opt.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            <div className="bg-[#1F2937] rounded-xl border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#111827]">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Date</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Email</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Produit</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Montant</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Statut</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-500 text-sm">
                        Aucune commande sur cette période.
                      </td>
                    </tr>
                  )}
                  {orders.map(order => {
                    const badge = getOrderTypeBadge(order);
                    return (
                      <tr key={order.stripe_session_id || order.order_id} className="border-t border-gray-700">
                        <td className="px-4 py-3 text-gray-400">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-white">{order.email_acheteur}</td>
                        <td className="px-4 py-3">
                          <span
                            data-testid={`order-type-${order.order_id || order.stripe_session_id}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${badge.classes}`}
                          >
                            {badge.isUpgrade && <ChevronUp className="w-3 h-3" />}
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white">{(order.montant / 100).toFixed(0)}€</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            order.statut === 'paid' ? 'bg-green-500/20 text-green-400' :
                            order.statut === 'manual' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-gray-700 text-gray-400'
                          }`}>
                            {order.statut}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400">{order.tokens_count || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Tokens View */}
        {activeTab === 'tokens' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Tokens d&apos;activation</h2>
              <button
                onClick={() => setShowManualAccess(true)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Créer accès manuel
              </button>
            </div>
            
            <div className="bg-[#1F2937] rounded-xl border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#111827]">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Token</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Email</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Statut</th>
                    <th className="px-4 py-3 text-left text-gray-400 text-sm">Expiration</th>
                    <th className="px-4 py-3 text-right text-gray-400 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map(token => (
                    <tr key={token._id} className="border-t border-gray-700">
                      <td className="px-4 py-3 text-gray-400 font-mono text-sm">{token.token_masked}</td>
                      <td className="px-4 py-3 text-white">{token.email_destinataire || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          token.used ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {token.used ? 'Utilisé' : 'En attente'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400">
                        {token.expires_at ? new Date(token.expires_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!token.used && (
                          <button
                            onClick={() => handleRegenerateToken(token._id)}
                            className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
                            title="Régénérer et renvoyer"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Settings View */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Paramètres</h2>
            
            <div className="bg-[#1F2937] rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4">Configuration</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#111827] rounded-lg">
                  <span className="text-gray-400">Stripe configuré</span>
                  <span
                    data-testid="admin-stripe-status"
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      health?.stripe_configured ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {health?.stripe_configured ? '✅ Oui' : '❌ Non'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-[#111827] rounded-lg">
                  <span className="text-gray-400">Brevo configuré</span>
                  <span
                    data-testid="admin-brevo-status"
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      health?.brevo_configured ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                    }`}
                  >
                    {health?.brevo_configured ? '✅ Oui' : '⚠️ Mode mock'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-[#111827] rounded-lg">
                  <span className="text-gray-400">JWT configuré</span>
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-green-500/20 text-green-400">
                    ✅ Oui
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Modals */}
      {selectedUser && (
        <UserModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
      
      {showManualAccess && (
        <ManualAccessModal 
          onClose={() => setShowManualAccess(false)}
          onSubmit={handleCreateManualAccess}
        />
      )}
    </div>
  );
}
